import AppHelper from '@Common/ApplicationCore/AppHelper';
import constants from '@Common/Core/Constant';
import { findPropertyById } from '@Common/Enums';
import { LaneCaptionType, ProcessChartLayout, TimeType, TransitionOrientation, TransitionTimeData, TrellisMetricType } from '@Common/Enums/Enum';
import documentHelper from '@Common/Helpers/DocumentHelper';
import {
    ComponentCoordinates,
    GeometryPoint,
    Process,
    ProcessChartAppearance,
    ProcessFilterDefinition,
    ProcessPath,
    ProcessSelectionOptions,
    ProcessStep,
    ProcessStepTransition,
    ProcessTransition,
    ProcessTransitionData,
    SubProcess,
    SwimlaneChartAppearance,
    SwimlaneGroup
} from '@InstanceFactory';
import formatHelper, { IFormat } from '@Common/Helpers/FormatHelper';

export namespace processChartsHelper {
    /*
     * Formatting
     */

    export function formatTransitionLabel(
        transitionData: ProcessTransition | ProcessTransitionData | ProcessPath,
        chartAppearance: ProcessChartAppearance | SwimlaneChartAppearance
    ): { firstLine: string; secondLine?: string } {
        let timeText = null;

        if (chartAppearance.transitionTimeData !== TransitionTimeData.none) {
            const currentValue = getDataByType(transitionData, chartAppearance.transitionTimeData);

            if (currentValue != null) {
                timeText = formatTimeSpanByType(currentValue, chartAppearance.timeType);
            }
        }

        if (isSwimlaneAppearance(chartAppearance)) {
            return {
                firstLine: timeText
            };
        } else {
            const returnText = {
                firstLine: null,
                secondLine: null
            };

            if (!chartAppearance.showTransitionLabels && !chartAppearance.displayMetricValues) {
                returnText.firstLine = '';
            } else if (!transitionData) {
                returnText.firstLine = '0';
            } else {
                if (isTransitionData(transitionData)) {
                    if (chartAppearance.displayMetricValues) {
                        returnText.firstLine = formatHelper.formatNumberByFormatName(transitionData.metricValue, chartAppearance.countFormatName);
                    } else {
                        returnText.firstLine = formatHelper.formatNumberByFormatName(transitionData.count, constants.numberNoDecimalFormatName);
                    }
                }

                if (timeText) {
                    returnText.secondLine = '({0})'.format(timeText);
                }
            }

            return returnText;
        }
    }

    export function formatTooltip(
        transitionData: ProcessTransitionData | ProcessPath,
        chartAppearance: ProcessChartAppearance | SwimlaneChartAppearance
    ): string {
        let returnText = '';

        if (transitionData) {
            returnText += `${(transitionData as any).fromStepTitle || ''} → ${(transitionData as any).toStepTitle || ''}<br />`;

            if (isTransitionData(transitionData)) {
                returnText += transitionData ? formatHelper.formatNumberByFormatName(transitionData.count, chartAppearance.countFormatName) : '0';
            }

            const tooltipItems = isSwimlaneAppearance(chartAppearance) ? chartAppearance.tooltipTimeData : chartAppearance.tooltipTimeDataItems;

            if (transitionData && transitionData.timeStatistics) {
                _(tooltipItems)
                    .uniq()
                    .forEach((value: TransitionTimeData, index) => {
                        if (value !== TransitionTimeData.none) {
                            const tooltipData = getDataByType(transitionData, value);

                            if (tooltipData != null) {
                                returnText +=
                                    (returnText.length ? '<br />' : '') +
                                    '{0}: {1}'.format(TransitionTimeData.getName(value), formatTimeSpanByType(tooltipData, chartAppearance.timeType));
                            }
                        }
                    });
            }
        }

        return returnText;
    }

    export function formatStepTooltip(data: IStepTooltipData): string {
        const format = formatHelper.getFormat(constants.numberNoDecimalFormatName);

        return strings.processChartsHelper.stepTooltipFormat.format(
            data.title,
            formatHelper.formatNumberByFormat(data.instancesCount, format),
            formatHelper.formatNumberByFormat(data.startedFromCount, format),
            formatHelper.formatNumberByFormat(data.incomingCount, format),
            formatHelper.formatNumberByFormat(data.outgoingCount, format)
        );
    }

    export function formatLaneCaption(pathData: ProcessPath, captionType: LaneCaptionType, format: IFormat): string {
        let value = '';

        switch (captionType) {
            case LaneCaptionType.max:
                value = formatTimeValue(pathData.timeStatistics.maxTime);
                break;
            case LaneCaptionType.min:
                value = formatTimeValue(pathData.timeStatistics.minTime);
                break;
            case LaneCaptionType.average:
                value = formatTimeValue(pathData.timeStatistics.averageTime);
                break;
            case LaneCaptionType.cost:
                value = formatHelper.formatNumberByFormat(pathData.cost * pathData.instancesCount, format);
                break;
            case LaneCaptionType.deviation:
                value = formatTimeValue(pathData.timeStatistics.stdDevTime);
                break;
            case LaneCaptionType.median:
                value = formatTimeValue(pathData.timeStatistics.medianTime);
                break;
            case LaneCaptionType.percentile75:
                value = formatTimeValue(pathData.timeStatistics.percentile75Time);
                break;
            case LaneCaptionType.percentile90:
                value = formatTimeValue(pathData.timeStatistics.percentile90Time);
                break;
        }

        return value;
    }

    export function getDataByType(transitionData: ProcessTransition | ProcessTransitionData | ProcessPath, timeData: TransitionTimeData): any {
        if (transitionData) {
            switch (timeData) {
                case TransitionTimeData.average:
                    return transitionData.timeStatistics.averageTime;
                case TransitionTimeData.deviation:
                    return transitionData.timeStatistics.stdDevTime;
                case TransitionTimeData.median:
                    return transitionData.timeStatistics.medianTime;
                case TransitionTimeData.percentile75:
                    return transitionData.timeStatistics.percentile75Time;
                case TransitionTimeData.percentile90:
                    return transitionData.timeStatistics.percentile90Time;
                case TransitionTimeData.none:
                    if (isTransitionData(transitionData)) {
                        return transitionData.count;
                    } else {
                        return null;
                    }
            }
        }
        return null;
    }

    export function getMetricDataByType(transitionData: ProcessTransitionData, timeData: TrellisMetricType): number {
        switch (timeData) {
            case TrellisMetricType.average:
                return transitionData.timeStatistics.averageTime / 1000;
            case TrellisMetricType.deviation:
                return transitionData.timeStatistics.stdDevTime / 1000;
            case TrellisMetricType.median:
                return transitionData.timeStatistics.medianTime / 1000;
            case TrellisMetricType.percentile75:
                return transitionData.timeStatistics.percentile75Time / 1000;
            case TrellisMetricType.percentile90:
                return transitionData.timeStatistics.percentile90Time / 1000;
            case TrellisMetricType.max:
                return transitionData.timeStatistics.maxTime / 1000;
            case TrellisMetricType.min:
                return transitionData.timeStatistics.minTime / 1000;
            case TrellisMetricType.metric:
                if (isTransitionData(transitionData)) {
                    return transitionData.metricValue;
                } else {
                    return null;
                }
            case TrellisMetricType.count:
                if (isTransitionData(transitionData)) {
                    return transitionData.count;
                } else {
                    return null;
                }
        }
        return null;
    }

    export function formatTimeSpanByType(value: number, timeType: TimeType): string {
        if (timeType === TimeType.timestamp) {
            return formatTimeValue(value);
        } else {
            const type = findPropertyById(processTimeTypes, timeType);
            return formatHelper.formatValueByDefault(value / type.value) + _.toLower(type.text[0]);
        }
    }

    export function formatTimeValue(value: number): string {
        const format = value >= processTimeTypes.days.value ? constants.dayTimeSpanFormat : constants.defaultTimeSpanFormat;

        return formatHelper.formatMilliseconds(value, format);
    }

    /*
     * Swimlane
     */

    export function isMinTransition(
        pathData: ProcessTransition | ProcessPath,
        transitions: (ProcessTransition | ProcessPath)[],
        timeData: TransitionTimeData
    ): boolean {
        const min = _.minBy(transitions, data => getDataByType(data, timeData));
        return pathData === min;
    }

    export function isMaxTransition(
        pathData: ProcessTransition | ProcessPath,
        transitions: (ProcessTransition | ProcessPath)[],
        timeData: TransitionTimeData
    ): boolean {
        const max = _.maxBy(transitions, data => getDataByType(data, timeData));
        return pathData === max;
    }

    /*
     * Steps utils
     */

    export function getStepFromSelection(processSelectionOptions: Partial<ProcessSelectionOptions>): number {
        if (processSelectionOptions.step !== undefined) {
            return processSelectionOptions.step;
        }
        if (processSelectionOptions.transition) {
            if (processSelectionOptions.transition.from === null) {
                return processSelectionOptions.transition.to;
            } else if (processSelectionOptions.transition.to === null) {
                return processSelectionOptions.transition.from;
            }
        }
        return null;
    }

    export function getStepByIndex(index: number, process: Process): ProcessStep {
        if (process && process.steps && process.steps.length) {
            return process.steps.find(step => step.number === index);
        }
        return null;
    }

    export function getStepNameByIndex(index: number, process: Process): string {
        const step = getStepByIndex(index, process);
        if (step) {
            return getStepTitle(step);
        }
        return '';
    }

    export function getStepTitle(stepData: ProcessStep): string {
        if (stepData.localizedTitle) {
            return stepData.localizedTitle;
        }
        if (stepData.title) {
            return stepData.title;
        }
        if (stepData.fieldId && stepData.fieldValue) {
            return stepData.fieldDisplayValue ? stepData.fieldDisplayValue : stepData.fieldValue;
        }
        if (stepData.record != null) {
            return stepData.record.name;
        }
        return strings.processDialog.newStep;
    }

    export function getNewStepNumber(process: Process): number {
        return (
            _(process.steps)
                .map(step => step.number - (step.number % 10) + 10)
                .max() || 0
        );
    }

    export function isAsyncStep(index: number, process: Process): boolean {
        const step = getStepByIndex(index, process);
        return step.isAsynchronous;
    }

    /*
     * Subprocesses
     */

    export function getParentSubProcess(process: Process, stepData: ProcessStep): SubProcess {
        return _.find(process.subProcesses, sp => sp.steps.contains(stepData.number));
    }

    export function isSubProcessStep(stepData: ProcessStep): boolean {
        return stepData && stepData.subProcessId && stepData.subProcessId !== constants.guidEmpty;
    }

    export function getSubProcessNameById(id: Guid, process: Process): string {
        if (process && process.subProcesses) {
            return (_.find(process.subProcesses, sp => sp.id === id) || { name: id }).name;
        }
        return '';
    }

    export function getSubProcess(process: Process, subProcessId: string): SubProcess {
        return _.find(process.subProcesses, sp => sp.id === subProcessId);
    }

    export function getSubProcessIncomingTransitions(process: Process, subProcess: SubProcess): ProcessStepTransition[] {
        return _.filter(
            process.transitions,
            transition => !subProcess.steps.contains(transition.sourceNumber) && subProcess.steps.contains(transition.sinkNumber)
        );
    }

    export function getSubProcessOutgoingTransitions(process: Process, subProcess: SubProcess): ProcessStepTransition[] {
        return _.filter(
            process.transitions,
            transition => subProcess.steps.contains(transition.sourceNumber) && !subProcess.steps.contains(transition.sinkNumber)
        );
    }

    export function getTransitionForSubStep(preparedProcess: Process, subProcess: SubProcess, startStep: ProcessStep): ProcessStepTransition {
        const transitionOutgoing = getSubProcessOutgoingTransitions(preparedProcess, subProcess)[0];
        const stepOutgoing = transitionOutgoing && getStepByIndex(transitionOutgoing.sinkNumber, preparedProcess);

        if (stepOutgoing) {
            const detectedSides = getNearestSidesOfRectangles(startStep.coordinates, stepOutgoing.coordinates);

            return {
                sourceNumber: startStep.number,
                sourceOrientation: detectedSides.sourceOrientation,
                sinkNumber: transitionOutgoing.sinkNumber,
                sinkOrientation: detectedSides.sinkOrientation,
                tolerance: null
            };
        } else {
            return null;
        }
    }

    export function prepareSubProcess(process: Process, subProcessId: string, rootProcesses?: string[]): Process {
        let preparedProcess = Process.create();
        let subProcessTransitions;
        preparedProcess.name = process.name;
        preparedProcess.id = process.id;
        preparedProcess.steps = process.steps;
        preparedProcess.transitions = process.transitions;

        if (subProcessId == null) {
            subProcessId = constants.guidEmpty;
        }

        if (subProcessId !== constants.guidEmpty) {
            const subProcess = getSubProcess(process, subProcessId);

            if (subProcess) {
                const stepsEnumerable = _(preparedProcess.steps).filter(step => subProcess.steps.contains(step.number));
                const minX = stepsEnumerable.minBy(step => step.coordinates.x1).coordinates.x1;
                const minY = stepsEnumerable.minBy(step => step.coordinates.y1).coordinates.y1;
                const gridSize = AppHelper.cookieStudioSettings().gridSize * 5;

                preparedProcess.steps = stepsEnumerable
                    .map(step =>
                        $.extend({}, step, {
                            coordinates: {
                                x1: step.coordinates.x1 - minX + gridSize,
                                y1: step.coordinates.y1 - minY + gridSize,
                                x2: step.coordinates.x2 - minX + gridSize,
                                y2: step.coordinates.y2 - minY + gridSize
                            }
                        })
                    )
                    .value();

                subProcessTransitions = stepsEnumerable
                    .filter(step => step.subProcessId != null)
                    .map(step => {
                        const sp = getSubProcess(process, step.subProcessId);

                        return getTransitionForSubStep(preparedProcess, sp, step);
                    })
                    .compact()
                    .value();

                preparedProcess.transitions = _.chain(preparedProcess.transitions)
                    .filter(transition => subProcess.steps.contains(transition.sourceNumber) && subProcess.steps.contains(transition.sinkNumber))
                    .union(subProcessTransitions)
                    .value();
            }
        } else {
            if (process.subProcesses.length) {
                preparedProcess.subProcesses = process.subProcesses;

                _.forEach(process.subProcesses, sp => {
                    if (rootProcesses == null || !rootProcesses.contains(sp.id)) {
                        preparedProcess.steps = _.filter(preparedProcess.steps, step => !sp.steps.contains(step.number));

                        subProcessTransitions = _.chain(preparedProcess.steps)
                            .filter(step => step.subProcessId === sp.id)
                            .map(step => getTransitionForSubStep(preparedProcess, sp, step))
                            .compact()
                            .value();

                        preparedProcess.transitions = _.chain(preparedProcess.transitions)
                            .filter(transition => !sp.steps.contains(transition.sourceNumber) && !sp.steps.contains(transition.sinkNumber))
                            .union(subProcessTransitions ? subProcessTransitions : null)
                            .value();
                    }
                });
            } else {
                preparedProcess = process;
            }
        }

        return preparedProcess;
    }

    export function getNearestSidesOfRectangles(
        coordinatesSource: ComponentCoordinates,
        coordinatesSink: ComponentCoordinates
    ): Partial<ProcessStepTransition> {
        return _.chain(TransitionOrientation.getValues())
            .flatMap(sideSource =>
                _.map(TransitionOrientation.getValues(), sideSink => ({
                    sourceOrientation: sideSource.id,
                    pointSource: getPointBySide(sideSource.id, coordinatesSource),
                    sinkOrientation: sideSink.id,
                    pointSink: getPointBySide(sideSink.id, coordinatesSink)
                }))
            )
            .filter(transition => transition.pointSource != null && transition.pointSink != null)
            .orderBy(transition => getDistanceBetweenPoints(transition.pointSource, transition.pointSink))
            .take()
            .value()[0];
    }

    export function getPointBySide(side: TransitionOrientation, coordinates: ComponentCoordinates): IPoint {
        switch (side) {
            case TransitionOrientation.top:
                return {
                    x: coordinates.x1 + (coordinates.x2 - coordinates.x1) / 2,
                    y: coordinates.y1
                };
            case TransitionOrientation.right:
                return {
                    x: coordinates.x2,
                    y: coordinates.y1 + (coordinates.y2 - coordinates.y1) / 2
                };
            case TransitionOrientation.bottom:
                return {
                    x: coordinates.x1 + (coordinates.x2 - coordinates.x1) / 2,
                    y: coordinates.y2
                };
            case TransitionOrientation.left:
                return {
                    x: coordinates.x1,
                    y: coordinates.y1 + (coordinates.y2 - coordinates.y1) / 2
                };
            default:
                return null;
        }
    }

    export function getDistanceBetweenPoints(pointSource: GeometryPoint, pointSink: GeometryPoint): number {
        return Math.sqrt((pointSink.x -= pointSource.x) * pointSink.x + (pointSink.y -= pointSource.y) * pointSink.y);
    }

    /*
     * Documents
     */

    export function getSwimlaneGroups(processId: Guid, projectId: Guid): SwimlaneGroup[] {
        return _.filter(
            documentHelper.getSwimlaneGroups(projectId),
            swimlaneGroup => swimlaneGroup.process && swimlaneGroup.process.id === processId
        );
    }

    export function getSwimlaneGroupsNames(processId: Guid, projectId: Guid): string[] {
        return _.map(getSwimlaneGroups(processId, projectId), group => group.name);
    }

    export function getProcessFilterDefinitions(processId: Guid, projectId: Guid): ProcessFilterDefinition[] {
        const allPFD = documentHelper.getProcessFilterDefinitions(projectId) || [];
        return allPFD.filter(pfd => pfd.process && pfd.process.id === processId);
    }

    export function setAutoLayoutForChart(appearance: ProcessChartAppearance, process: Process): void {
        appearance.autoLayout = process.isAutogenerated ? ProcessChartLayout.horizontal : ProcessChartLayout.none;
    }

    /*
     * Helper functions
     */

    function isTransitionData(object: ProcessTransition | ProcessTransitionData | ProcessPath): object is ProcessTransitionData {
        return (object as ProcessTransitionData).count !== undefined;
    }

    function isSwimlaneAppearance(object: ProcessChartAppearance | SwimlaneChartAppearance): object is SwimlaneChartAppearance {
        return (object as SwimlaneChartAppearance).tooltipTimeData !== undefined;
    }

    export const processTimeTypes = {
        days: { value: 86400000, text: strings.processChartsHelper.days, id: 'Days' },
        hours: { value: 3600000, text: strings.processChartsHelper.hours, id: 'Hours' },
        minutes: { value: 60000, text: strings.processChartsHelper.minutes, id: 'Minutes' },
        seconds: { value: 1000, text: strings.processChartsHelper.seconds, id: 'Seconds' }
    };

    export interface IStepTooltipData {
        title: string;
        instancesCount: number;
        startedFromCount: number;
        incomingCount: number;
        outgoingCount: number;
    }
}
